export interface SchoolData {
  id: number;
  schoolCode: string;
  county: string;
  district: string;
  schoolName: string;
  kindergartners: number;
  schoolType: string;
  allVaccinations: number;
  mmr: number;
  medicalExemption: number;
}

/**
 * Parses the CSV data and maps it to the SchoolData interface
 * @param csvText The raw CSV text data
 * @returns An array of SchoolData objects
 */
export function parseVaccinationData(csvText: string): SchoolData[] {
  // Split the CSV into lines
  const lines = csvText.split("\n");

  // Skip the header row
  const dataLines = lines.slice(1);

  // Initialize the result array with a reasonable capacity
  const result: SchoolData[] = [];

  // Process each line of the CSV
  let id = 1;

  // Create parsing function for better performance
  const parseFloat = window.parseFloat;
  const parseInt = window.parseInt;

  // Pre-allocate indices for better performance
  const SCHOOL_CODE_IDX = 0;
  const SCHOOL_TYPE_IDX = 1;
  const COUNTY_IDX = 3;
  const DISTRICT_IDX = 4;
  const SCHOOL_NAME_IDX = 5;
  const KINDERGARTENERS_IDX = 6;
  const UTD_IDX = 8;
  const PME_IDX = 9;
  const MMR_IDX = 10;

  for (let i = 0; i < dataLines.length; i++) {
    const line = dataLines[i];
    // Skip empty lines
    if (!line.trim()) continue;

    // Split the line by comma
    const columns = line.split(",");

    // Check if we have the required vaccination data
    // Skip rows that don't have UTD, MMR, or PME values
    const utd = parseFloat(columns[UTD_IDX]);
    const pme = parseFloat(columns[PME_IDX]);
    const mmr = parseFloat(columns[MMR_IDX]);

    // Skip rows with missing vaccination data
    if (isNaN(utd) || isNaN(pme) || isNaN(mmr)) {
      continue;
    }

    // Create a SchoolData object
    const schoolData: SchoolData = {
      id: id++,
      schoolCode: columns[SCHOOL_CODE_IDX] || "", // SchoolCode
      county: columns[COUNTY_IDX] || "", // County
      district: columns[DISTRICT_IDX] || "", // District
      schoolName: columns[SCHOOL_NAME_IDX] || "", // School
      kindergartners: parseInt(columns[KINDERGARTENERS_IDX]) || 0, // Enrollment 2023-24
      schoolType: columns[SCHOOL_TYPE_IDX] || "", // Type
      allVaccinations: utd, // UTD
      mmr: mmr, // MMR
      medicalExemption: pme, // PME
    };

    // Add to results
    result.push(schoolData);
  }

  return result;
}

/**
 * Loads the vaccination data from the CSV file
 * This function should be called to initialize the data
 *
 * @returns A promise that resolves to an array of SchoolData objects
 */
export async function loadVaccinationData(): Promise<SchoolData[]> {
  // Use memoization to avoid loading the CSV multiple times
  if (loadVaccinationData.cachedData) {
    return loadVaccinationData.cachedData;
  }

  try {
    // Path for public file access in React
    const response = await fetch("./files/vaccinationTabledata.csv");
    if (!response.ok) {
      throw new Error(
        `Failed to load CSV data: ${response.status} ${response.statusText}`
      );
    }

    const csvText = await response.text();
    loadVaccinationData.cachedData = parseVaccinationData(csvText);
    return loadVaccinationData.cachedData;
  } catch (error) {
    console.error("Error loading vaccination data:", error);
    // Return empty array instead of mock data
    return [];
  }
}

// Add cachedData property to the function for memoization
loadVaccinationData.cachedData = null as SchoolData[] | null;

// Export an empty array instead of mock data
export const schoolVaccinationData: SchoolData[] = [];
